import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../../components/MDDForm/components/ContentDialog.jsx";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import { moveObjekt } from "@accredo/fe-shared/services/objektMoveService.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";

const matchesSearch = (object, term) => {
  if (!term) return true;
  const haystack =
    `${object.bezeichnung ?? ""} ${object.customer_reference_number ?? ""} ${object.strasse ?? ""} ${object.hausnr ?? ""} ${object.plz ?? ""} ${object.ort ?? ""}`.toLowerCase();
  return haystack.includes(term.toLowerCase());
};

const buildObjectColumns = (objectRefNumLabel) => [
  { accessorKey: "bezeichnung", header: "Bezeichnung", size: 200 },
  ...(objectRefNumLabel
    ? [
        {
          accessorKey: "customer_reference_number",
          header: objectRefNumLabel,
          size: 100,
        },
      ]
    : []),
  { accessorKey: "strasse", header: "Straße", size: 150 },
  { accessorKey: "hausnr", header: "Hausnr.", size: 90 },
  { accessorKey: "plz", header: "PLZ", size: 80 },
  { accessorKey: "ort", header: "Ort", size: 150 },
  {
    accessorKey: "vertrag",
    header: "Bisheriger Vertrag",
    size: 160,
    Cell: ({ row }) => row.original.vertrag?.vertragsnummer || "–",
  },
];

// Kurzer Spaltentitel mit Tooltip statt eines langen "X (...)"-Headers - Klartext
// steht bei Bedarf im Tooltip, nicht dauerhaft in der Spaltenüberschrift.
const headerWithTooltip = (label, tooltip) => ({
  header: label,
  Header: () => (
    <Tooltip title={tooltip}>
      <span>{label}</span>
    </Tooltip>
  ),
});

// Spalten der Bestätigungs-Vorschau (PREVIEW_COLUMNS): pro ausgewähltem Objekt das
// Ergebnis des dry_run-Aufrufs von ObjektController::move() - was tatsächlich
// mitverschoben würde, und ob schon vorher Schäden am Ziel-Vertrag für dieses Objekt
// existierten (siehe Backend-Kommentar zu existing_schaden_at_target_count). Adresse
// (Straße/Hausnr./PLZ/Ort) wird hier bewusst ausgeblendet - für die Bestätigung nicht
// relevant, das Objekt ist über Bezeichnung/Ref-ID/bisherigen Vertrag schon eindeutig.
const buildPreviewColumns = (objectRefNumLabel) => [
  ...buildObjectColumns(objectRefNumLabel).filter(
    (col) => !["strasse", "hausnr", "plz", "ort"].includes(col.accessorKey)
  ),
  {
    accessorKey: "sparten_count",
    ...headerWithTooltip("Sparten", "Anzahl Sparten, die mitziehen würden"),
    size: 90,
    Cell: ({ row }) => row.original.preview?.sparten_count ?? "–",
  },
  {
    accessorKey: "schaden_count",
    ...headerWithTooltip("Schäden", "Anzahl Schäden, die mitziehen würden"),
    size: 90,
    Cell: ({ row }) => row.original.preview?.schaden_count ?? "–",
  },
  {
    accessorKey: "error",
    header: "Fehler",
    size: 200,
    Cell: ({ row }) =>
      row.original.error ? (
        <Typography variant="body2" color="error">
          {row.original.error}
        </Typography>
      ) : (
        "–"
      ),
  },
];

const SCHADEN_DETAIL_COLUMNS = [
  {
    accessorKey: "schaden_nummer_accredo",
    header: "Schadennummer",
    size: 150,
    Cell: ({ row }) => {
      const schaden = row.original;
      return (
        <a
          href={`/schaeden/${schaden.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {schaden.schaden_nummer_accredo || schaden.id}
        </a>
      );
    },
  },
  { accessorKey: "status_label", header: "Status", size: 150 },
  {
    accessorKey: "schaden_datum",
    header: "Schadendatum",
    size: 120,
    Cell: ({ row }) => getDatum(row.original.schaden_datum),
  },
];

// Aufklappbares Detail-Panel je Objekt-Zeile in der Bestätigungs-Vorschau: die
// tatsächlichen Schäden, die mitverschoben würden (nicht nur die Anzahl), mit
// Absprung zum jeweiligen Schaden in einem neuen Browser-Tab.
function renderSchadenDetailPanel({ row }) {
  const schadens = row.original.preview?.schadens || [];

  if (schadens.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Keine Schäden an diesem Objekt.
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <DataTable
        columns={SCHADEN_DETAIL_COLUMNS}
        data={schadens}
        hasLoaded
        enableTopToolbar={false}
        enablePagination={false}
        enableColumnFilters={false}
        enableColumnHiding={false}
        enableSorting={false}
      />
    </Box>
  );
}

/**
 * Zeigt vor der eigentlichen Zuordnung je ausgewähltem Objekt die dry_run-Vorschau
 * (was würde verschoben, gäbe es schon Schäden am Ziel) zur Bestätigung - Muster:
 * die Bulk-Zuordnungs-Vorschau bei Bankkonten (components/modals/BulkAssignObjectsDialog.jsx),
 * hier um die move()-Vorschaudaten erweitert. Objekte mit Fehler (z. B. durch eine
 * zwischenzeitliche Änderung nicht mehr gültig) werden bei der Bestätigung
 * übersprungen, nicht die gesamte Aktion blockiert.
 */
function SaveConfirmDialog({
  open,
  onClose,
  previewResults,
  isSaving,
  onConfirm,
  objectRefNumLabel,
}) {
  const previewColumns = useMemo(
    () => buildPreviewColumns(objectRefNumLabel),
    [objectRefNumLabel]
  );
  const rows = previewResults.map((r) => ({
    ...r.object,
    preview: r.preview,
    error: r.error,
  }));
  const valid_count = previewResults.filter((r) => !r.error).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Vorschau: Objekte zuordnen</DialogTitle>
      <DialogContent>
        {rows.length === 0 ? (
          <Typography variant="body2">Keine Objekte ausgewählt.</Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Noch nichts wurde geändert. Diese Objekte würden bei Bestätigung
              dem Vertrag zugeordnet. Bei den betroffenen Schäden werden
              Vertragsnummer/Gesellschaft im Schadenformular korrigiert; für
              Schäden im Status „gespeichert"/„in Bearbeitung" wird zusätzlich
              automatisch ein neues Schadenformular-PDF erzeugt.
            </Typography>
            {valid_count < rows.length && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {rows.length - valid_count} von {rows.length} Objekten können
                nicht zugeordnet werden (siehe Spalte „Fehler") und werden
                übersprungen.
              </Alert>
            )}
            <DataTable
              columns={previewColumns}
              data={rows}
              hasLoaded
              renderDetailPanel={renderSchadenDetailPanel}
              enableTopToolbar={false}
              enablePagination={false}
              enableColumnFilters={false}
              enableColumnHiding={false}
              enableSorting={false}
              enableStickyHeader
              muiTableContainerProps={{
                sx: {
                  maxHeight: 320,
                  overflowX: "auto",
                  overflowY: "auto",
                  borderLeft: 0,
                  borderRight: 0,
                },
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          disabled={isSaving || valid_count === 0}
          onClick={onConfirm}
        >
          {isSaving ? "Wird zugeordnet..." : "Jetzt zuordnen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SaveConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  previewResults: PropTypes.arrayOf(
    PropTypes.shape({
      object: PropTypes.object.isRequired,
      preview: PropTypes.object,
      error: PropTypes.string,
    })
  ).isRequired,
  isSaving: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  objectRefNumLabel: PropTypes.string,
};

/**
 * Ordnet mehrere Objekte dieses Kunden dem Vertrag zu, aus dessen Zeile der Dialog
 * geöffnet wurde (ACC-321, Korrektur einer Fehlzuordnung). Mehrfachauswahl + Filter
 * (Suche/Typ/bisheriger Vertrag) analog der Bankkonten-Objekt-Zuweisung (manuell,
 * ACC-323) - hier aber als reine Einweg-Zuordnung (kein Abwählen
 * bereits zugeordneter Objekte, da jedes Objekt immer genau einem Vertrag gehört und
 * es kein "unassigned" gibt). Objekte, die bereits zu diesem Vertrag gehören, werden
 * nicht angeboten. Pro ausgewähltem Objekt ein sequenzieller move()-Aufruf (ACC-321).
 */
export default function AssignObjectToContractDialog({
  open,
  onClose,
  customerId,
  vertrag,
  onSuccess,
  objectRefNumLabel,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();
  const objectColumns = useMemo(
    () => buildObjectColumns(objectRefNumLabel),
    [objectRefNumLabel]
  );

  const [objects, setObjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [vertragFilter, setVertragFilter] = useState("");
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [previewResults, setPreviewResults] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }

    setSelectedIds([]);
    setSearchTerm("");
    setTypeFilter("");
    setVertragFilter("");
    setShowOnlySelected(false);
    setShowSaveConfirm(false);
    setPreviewResults([]);

    backendGet(`data/kundes/${customerId}/objects`, {
      label: "Objekte",
      setResult: (res) => {
        // Objekte, die bereits zu diesem Vertrag gehören, bringen für "Zuordnen" nichts.
        const loaded = (res.data || []).filter(
          (o) => o.vertrag?.id !== vertrag?.id
        );
        setObjects(loaded);
      },
      setIsLoading: setIsLoading,
      options: {
        params: { api_version: "v2", include: ["vertrag"] },
      },
    });

    setOpenCD(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, vertrag?.id]);

  const types = useMemo(
    () => Array.from(new Set(objects.map((o) => o.typ).filter(Boolean))).sort(),
    [objects]
  );

  const vertraege = useMemo(() => {
    const byId = new Map();
    objects.forEach((o) => {
      if (o.vertrag?.id != null) {
        byId.set(o.vertrag.id, o.vertrag.vertragsnummer);
      }
    });
    return Array.from(byId, ([id, vertragsnummer]) => ({
      id,
      vertragsnummer,
    })).sort((a, b) =>
      (a.vertragsnummer || "").localeCompare(b.vertragsnummer || "")
    );
  }, [objects]);

  const visibleObjects = useMemo(() => {
    return objects
      .filter((o) => matchesSearch(o, searchTerm))
      .filter((o) => !typeFilter || o.typ === typeFilter)
      .filter(
        (o) =>
          !vertragFilter || String(o.vertrag?.id) === String(vertragFilter)
      )
      .filter((o) => !showOnlySelected || selectedIds.includes(o.id));
  }, [
    objects,
    searchTerm,
    typeFilter,
    vertragFilter,
    showOnlySelected,
    selectedIds,
  ]);

  const rowSelectionState = useMemo(
    () => Object.fromEntries(selectedIds.map((id) => [String(id), true])),
    [selectedIds]
  );

  const handleRowSelectionChange = (updaterOrValue) => {
    const next =
      typeof updaterOrValue === "function"
        ? updaterOrValue(rowSelectionState)
        : updaterOrValue;
    setSelectedIds(
      Object.keys(next)
        .filter((id) => next[id])
        .map(Number)
    );
  };

  const selectedObjects = useMemo(
    () => objects.filter((o) => selectedIds.includes(o.id)),
    [objects, selectedIds]
  );

  // Zwischenschritt vor der eigentlichen Zuordnung: für jedes ausgewählte Objekt
  // eine dry_run-Vorschau von move() einholen (was würde verschoben, gäbe es schon
  // Schäden am Ziel-Vertrag) und im Bestätigungsdialog anzeigen, statt blind zu
  // übernehmen.
  const runPreviewAndOpenConfirm = async () => {
    setIsLoadingPreview(true);
    const results = await Promise.all(
      selectedObjects.map(async (object) => {
        try {
          const preview = await moveObjekt(object.id, vertrag.id, {
            dryRun: true,
          });
          return { object, preview };
        } catch (error) {
          return {
            object,
            error: error?.response?.data?.message || "Vorschau fehlgeschlagen.",
          };
        }
      })
    );
    setIsLoadingPreview(false);
    setPreviewResults(results);
    setShowSaveConfirm(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const valid_results = previewResults.filter((r) => !r.error);
    const results = await Promise.allSettled(
      valid_results.map((r) =>
        moveObjekt(r.object.id, vertrag.id, { dryRun: false })
      )
    );
    setIsSaving(false);
    setShowSaveConfirm(false);

    const failed = results.filter((r) => r.status === "rejected").length;
    const succeeded = results.length - failed;
    const skipped = previewResults.length - valid_results.length;

    if (failed === 0 && skipped === 0) {
      enqueueSnackbar(`${succeeded} Objekt(e) diesem Vertrag zugeordnet.`, {
        variant: "success",
      });
    } else {
      const skipped_part = skipped > 0 ? `, ${skipped} übersprungen` : "";
      enqueueSnackbar(
        `${succeeded} Objekt(e) zugeordnet, ${failed} fehlgeschlagen${skipped_part}.`,
        { variant: succeeded > 0 ? "warning" : "error" }
      );
      if (failed > 0) {
        console.error(
          "Failed to assign some objects to contract",
          results.filter((r) => r.status === "rejected")
        );
      }
    }

    onSuccess?.();
  };

  useEffect(() => {
    setTca({
      title: "Objekte aus einem anderen Vertrag zuordnen",
      maxWidth: "lg",
      content: isLoading ? (
        <Spinner />
      ) : (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Vertrag {vertrag?.vertragsnummer}
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              label="Suche"
              placeholder={
                objectRefNumLabel
                  ? `Bezeichnung, ${objectRefNumLabel}, Adresse ...`
                  : "Bezeichnung, Adresse ..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small"
              select
              label="Typ"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Alle</MenuItem>
              {types.map((typ) => (
                <MenuItem key={typ} value={typ}>
                  {typ}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Bisheriger Vertrag"
              value={vertragFilter}
              onChange={(e) => setVertragFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Alle</MenuItem>
              {vertraege.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.vertragsnummer}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlySelected}
                  onChange={(e) => setShowOnlySelected(e.target.checked)}
                  size="small"
                />
              }
              label="Nur ausgewählte anzeigen"
            />
          </Box>

          {visibleObjects.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {objects.length === 0
                ? "Keine anderen Objekte für diesen Kunden vorhanden."
                : "Keine Objekte entsprechen den gewählten Filtern."}
            </Typography>
          ) : (
            <DataTable
              columns={objectColumns}
              data={visibleObjects}
              hasLoaded
              getRowId={(row) => row.id}
              enableRowSelection
              rowSelection={rowSelectionState}
              onRowSelectionChange={handleRowSelectionChange}
              enableTopToolbar={false}
              enablePagination={false}
              enableColumnFilters={false}
              enableColumnHiding={false}
              enableSorting={false}
              enableStickyHeader
              muiTableContainerProps={{
                sx: {
                  maxHeight: 400,
                  overflowX: "auto",
                  overflowY: "auto",
                  borderLeft: 0,
                  borderRight: 0,
                },
              }}
            />
          )}
        </Box>
      ),
      actions: (
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={runPreviewAndOpenConfirm}
            disabled={isLoading || isLoadingPreview || selectedIds.length === 0}
          >
            {isLoadingPreview ? "Vorschau wird geladen..." : "Weiter"}
          </Button>
        </>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vertrag,
    visibleObjects,
    rowSelectionState,
    isLoading,
    isLoadingPreview,
    searchTerm,
    typeFilter,
    vertragFilter,
    showOnlySelected,
    types,
    vertraege,
    selectedIds,
    objectColumns,
    objectRefNumLabel,
  ]);

  return (
    <>
      <ContentDialog
        openCD={openCD}
        setOpenCD={setOpenCD}
        tca={tca}
        onClose={onClose}
      />
      <SaveConfirmDialog
        open={showSaveConfirm}
        onClose={() => setShowSaveConfirm(false)}
        previewResults={previewResults}
        isSaving={isSaving}
        onConfirm={handleSave}
        objectRefNumLabel={objectRefNumLabel}
      />
    </>
  );
}

AssignObjectToContractDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vertrag: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id_ov: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vertragsnummer: PropTypes.string,
  }),
  objectRefNumLabel: PropTypes.string,
};
